/**
 * I dispositivi elettrici del socio: cosa ha attaccato al prelievo, quanti e
 * di che potenza.
 *
 * Servono a conoscere il socio, non ancora a disegnarne la curva: nessuna fonte
 * ufficiale dice quanto pesi una pompa di calore sul profilo di una famiglia, e
 * dedurlo da qui sarebbe inventarlo (vedi la regola sul prelievo in AGENTS.md).
 * La potenza è facoltativa: il socio spesso non la sa, e un dispositivo senza
 * potenza è comunque un'informazione.
 */

export const APPLIANCE_TYPES = [
	{ value: 'pompa_calore', label: 'Pompa di calore', ambito: 'casa' },
	{ value: 'climatizzatore', label: 'Climatizzatore', ambito: 'casa' },
	{ value: 'scaldabagno', label: 'Scaldabagno elettrico', ambito: 'casa' },
	{ value: 'piano_induzione', label: 'Piano a induzione', ambito: 'casa' },
	{ value: 'auto_elettrica', label: 'Auto elettrica / wallbox', ambito: 'casa' },
	{ value: 'piscina', label: 'Piscina o idromassaggio', ambito: 'casa' },
	{ value: 'macchinari', label: 'Macchinari', ambito: 'attivita' },
	{ value: 'celle_frigo', label: 'Celle o banchi frigoriferi', ambito: 'attivita' },
	{ value: 'forno_elettrico', label: 'Forno elettrico', ambito: 'attivita' },
	{ value: 'altro', label: 'Altro', ambito: 'casa' },
] as const;

export type ApplianceType = (typeof APPLIANCE_TYPES)[number]['value'];

export const APPLIANCE_LABELS: Record<ApplianceType, string> = Object.fromEntries(
	APPLIANCE_TYPES.map((t) => [t.value, t.label]),
) as Record<ApplianceType, string>;

export interface MemberAppliance {
	tipo: ApplianceType;
	/** Per `altro`: che cosa è. */
	nome?: string | null;
	quantita: number;
	/** Potenza di un singolo dispositivo, in kW. `null` se non si sa. */
	potenzaKw: number | null;
}

/** Al più tanti dispositivi: un elenco, non un inventario. */
export const MAX_APPLIANCES = 30;

/**
 * Controlla l'elenco che arriva da un form o dall'app e lo restituisce pulito,
 * oppure il primo errore da mostrare.
 */
export function normalizeAppliances(
	raw: unknown,
): { value: MemberAppliance[] } | { error: string } {
	if (raw == null) return { value: [] };
	if (!Array.isArray(raw)) return { error: 'Dispositivi: elenco non valido.' };
	if (raw.length > MAX_APPLIANCES) return { error: `Dispositivi: al massimo ${MAX_APPLIANCES}.` };
	const out: MemberAppliance[] = [];
	for (const item of raw) {
		const r = (item ?? {}) as Record<string, unknown>;
		const tipo = r.tipo as ApplianceType;
		if (!(tipo in APPLIANCE_LABELS)) return { error: 'Dispositivi: tipo non valido.' };
		const label = APPLIANCE_LABELS[tipo];
		const quantita = Number(r.quantita);
		if (!Number.isInteger(quantita) || quantita < 1)
			return { error: `${label}: la quantità è un numero intero da 1 in su.` };
		const potenza = r.potenzaKw == null || r.potenzaKw === '' ? null : Number(r.potenzaKw);
		if (potenza != null && !(Number.isFinite(potenza) && potenza > 0))
			return { error: `${label}: la potenza è un numero maggiore di 0.` };
		const nome = typeof r.nome === 'string' ? r.nome.trim().slice(0, 80) : '';
		if (tipo === 'altro' && !nome) return { error: 'Altro dispositivo: scrivi che cos’è.' };
		out.push({ tipo, ...(tipo === 'altro' ? { nome } : {}), quantita, potenzaKw: potenza });
	}
	return { value: out };
}
